import React from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { ArrowRight, TrendingUp } from "lucide-react";
import { getBlueprintTheme } from "./blueprintThemes";
import { BADGE_CONFIG } from "./badgeConfig";

export default function BlueprintCard({ blueprint, onCreateWorkflow, variant = "default" }) {
    const { t } = useTranslation();
    const { Icon, colors } = getBlueprintTheme(blueprint);
    const isApp = blueprint.category === "APP_V2";
    const isFeatured = variant === "featured";
    const isPopular = (blueprint.downloadCount || 0) > 5;
    const badges = blueprint.badges || [];

    return (
        <Card
            className={`group h-full flex flex-col transition-all duration-200 cursor-pointer ${colors.accent} ${
                isFeatured ? "border-2 border-gray-900 shadow-md hover:shadow-xl" : "border border-gray-200 hover:shadow-lg"
            }`}
            onClick={() => onCreateWorkflow(blueprint)}
        >
            <div className={`relative w-full ${isFeatured ? "aspect-[16/9]" : "aspect-[16/10]"} overflow-hidden bg-gradient-to-br ${colors.gradient}`}>
                {blueprint.imageUrl ? (
                    <>
                        <div
                            aria-hidden="true"
                            className="absolute inset-0 bg-center bg-cover scale-110 blur-2xl opacity-70 transition-transform duration-500 ease-out group-hover:scale-125"
                            style={{ backgroundImage: `url(${blueprint.imageUrl})` }}
                        />
                        <img
                            src={blueprint.imageUrl}
                            alt={blueprint.name}
                            loading="lazy"
                            className="relative w-full h-full object-contain transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                        />
                    </>
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Icon className={`${isFeatured ? "h-20 w-20" : "h-14 w-14"} ${colors.icon} opacity-80 transition-transform duration-500 ease-out group-hover:scale-110`} />
                    </div>
                )}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/25 to-transparent" />
                <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    <Badge className={`text-xs backdrop-blur-sm ${colors.badge}`} variant="outline">
                        {isApp ? t("blueprints.app") : t("blueprints.automation")}
                    </Badge>
                    {isPopular ? (
                        <Badge className="text-xs bg-amber-100/90 text-amber-700 border-amber-200 backdrop-blur-sm" variant="outline">
                            <TrendingUp className="h-3 w-3 mr-0.5" /> {t("blueprints.popular", "Popular")}
                        </Badge>
                    ) : null}
                    {badges.map((badge) => {
                        const cfg = BADGE_CONFIG[badge];
                        if (!cfg) return null;
                        const BIcon = cfg.icon;
                        return (
                            <Badge key={badge} className={`text-xs ${cfg.className}`} variant="outline">
                                <BIcon className="h-3 w-3 mr-0.5" /> {cfg.label}
                            </Badge>
                        );
                    })}
                </div>
            </div>
            <div className={`flex-1 flex flex-col ${isFeatured ? "p-5" : "p-4"}`}>
                <h3 className={`font-semibold leading-tight line-clamp-2 mb-1.5 ${isFeatured ? "text-lg" : "text-base"}`}>
                    {blueprint.name}
                </h3>
                <p className={`text-muted-foreground line-clamp-2 mb-3 ${isFeatured ? "text-sm" : "text-xs"}`}>
                    {blueprint.description || t("blueprints.noDescription")}
                </p>
                {blueprint.integrationTypes?.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mb-3">
                        {blueprint.integrationTypes.slice(0, 3).map((type) => (
                            <span key={type} className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{type}</span>
                        ))}
                        {blueprint.integrationTypes.length > 3 ? (
                            <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                                +{blueprint.integrationTypes.length - 3}
                            </span>
                        ) : null}
                    </div>
                ) : null}
                <div className="mt-auto pt-3 border-t flex items-center justify-between">
                    <span className="text-xs text-muted-foreground truncate mr-2">{blueprint.creatorName}</span>
                    <Button size="sm" className={isFeatured ? "bg-gray-900 text-white hover:bg-gray-800" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}>
                        {t("blueprints.createWorkflow")} <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                </div>
            </div>
        </Card>
    );
}

export function FeaturedBlueprintCard({ blueprint, onCreateWorkflow }) {
    return <BlueprintCard blueprint={blueprint} onCreateWorkflow={onCreateWorkflow} variant="featured" />;
}
